import api from '../api/client';
import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Input, Select, Button, Upload, message, DatePicker, Divider, Space, Card, Table, Typography, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Title, Text } = Typography;

export default function EggSaleForm({ stakeholders, onChange, employees }) {
    const [form] = Form.useForm();
    const [deliveredMethod, setDeliveredMethod] = useState('picked');
    const [items, setItems] = useState([{ type: 'normal', trays: 0, pricePerTray: 0 }]);
    const [brokenDuringSales, setBrokenDuringSales] = useState(0);

    const eggTypes = [
        { value: 'normal', label: 'Normal Eggs' },
        { value: 'medium', label: 'Medium Eggs' },
        { value: 'double', label: 'Double Yolk' },
        { value: 'broken', label: 'Broken Eggs' },
        { value: 'spoilt', label: 'Spoilt (Discarded)' }
    ];

    const paymentOptions = [
        { value: 'unpaid', label: 'On Credit' },
        { value: 'cash', label: 'On Cash' }
    ];

    // Get available egg types that haven't been selected yet
    const getAvailableEggTypes = (currentIndex) => {
        const selectedTypes = items
            .filter((_, index) => index !== currentIndex) // Exclude current item
            .map((item) => item.type)
            .filter(Boolean);

        return eggTypes.filter((egg) => !selectedTypes.includes(egg.value));
    };

    const addItem = () => {
        const lastItem = items[items.length - 1];

        // Validate last item before adding new one
        if (!lastItem.trays || lastItem.trays <= 0) {
            message.error('Please enter number of trays (must be greater than 0)');
            return;
        }

        if (!lastItem.pricePerTray || lastItem.pricePerTray <= 0) {
            message.error('Please enter price per tray (must be greater than 0)');
            return;
        }

        const availableTypes = getAvailableEggTypes(-1); // Get types for new item
        if (availableTypes.length === 0) {
            message.warning('All egg types have been added');
            return;
        }

        setItems([
            ...items,
            {
                type: availableTypes[0].value,
                trays: 0,
                pricePerTray: 0
            }
        ]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    const updateItem = (index, key, value) => {
        const updated = [...items];
        updated[index][key] = value;
        setItems(updated);
    };

    // Compute total
    const totalAmount = items.reduce((sum, it) => sum + (it.trays || 0) * (it.pricePerTray || 0), 0);

    // Handle form values change
    const handleFormChange = () => {
        const formValues = form.getFieldsValue();
        onChange({
            saleType: 'eggs',
            items,
            totalAmount,
            ...formValues
        });
    };

    useEffect(() => {
        handleFormChange();
    }, [items, deliveredMethod]);

    const computeSubtotal = (item) => {
        return Number(item.trays || 0) * Number(item.pricePerTray || 0);
    };

    const getEggTypeLabel = (type) => {
        return eggTypes.find((egg) => egg.value === type)?.label || type;
    };

    const isAddButtonDisabled = () => {
        const lastItem = items[items.length - 1];
        const availableTypes = getAvailableEggTypes(-1);

        return (
            !lastItem.trays || lastItem.trays <= 0 || !lastItem.pricePerTray || lastItem.pricePerTray <= 0 || availableTypes.length === 0
        );
    };

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                initialValues={{
                    saleDate: moment(),
                    deliveredMethod: 'picked'
                }}
            >
                <Form.Item name="stakeholderId" label="Customer" rules={[{ required: true }]}>
                    <Select placeholder="Select customer" showSearch optionFilterProp="children">
                        {stakeholders.map((s) => (
                            <Select.Option key={s.id} value={s.id}>
                                {s.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap style={{ width: '100%', justifyContent: 'start' }}>
                        <Form.Item name="saleDate" label="Sale Date" rules={[{ required: true }]}>
                            <DatePicker className="w-full" format="YYYY-MM-DD" onChange={handleFormChange} />
                        </Form.Item>
                        <Form.Item name="deliveredMethod" label="Delivery Method" rules={[{ required: true }]}>
                            <Select onChange={(value) => setDeliveredMethod(value)}>
                                <Select.Option value="delivered">Delivered</Select.Option>
                                <Select.Option value="picked">Picked at Farm</Select.Option>
                            </Select>
                        </Form.Item>

                        {deliveredMethod === 'delivered' && (
                            <Form.Item name="deliveryId" label="Delivery Person (Employee)" rules={[{ required: true }]}>
                                <Select placeholder="Select delivery person" showSearch optionFilterProp="children">
                                    {employees.map((e) => (
                                        <Select.Option key={e.id} value={e.id}>
                                            {e.firstname} {e.lastname}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Space>
                </Space>

                <Divider />

                <Title level={3}>Egg Items</Title>

                {items.map((item, index) => (
                    <Card
                        key={index}
                        style={{
                            border: '1px solid #d9d9d9',
                            marginBottom: 16,
                            borderRadius: 8
                        }}
                        size="small"
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Space wrap style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                                <Space wrap>
                                    <Form.Item
                                        label="Egg Type"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.type ? 'error' : ''}
                                        help={!item.type ? 'Egg type is required' : ''}
                                    >
                                        <Select
                                            value={item.type}
                                            onChange={(v) => updateItem(index, 'type', v)}
                                            options={getAvailableEggTypes(index)}
                                            style={{ width: 200 }}
                                            placeholder="Select egg type"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Number of Trays"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.trays || item.trays <= 0 ? 'error' : ''}
                                        help={!item.trays || item.trays <= 0 ? 'Must be greater than 0' : ''}
                                    >
                                        <InputNumber
                                            placeholder="Trays"
                                            min={1}
                                            value={item.trays}
                                            onChange={(v) => updateItem(index, 'trays', v)}
                                            style={{ width: 150 }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Price per Tray"
                                        style={{ marginBottom: 0 }}
                                        validateStatus={!item.pricePerTray || item.pricePerTray <= 0 ? 'error' : ''}
                                        help={!item.pricePerTray || item.pricePerTray <= 0 ? 'Must be greater than 0' : ''}
                                    >
                                        <InputNumber
                                            placeholder="Price"
                                            min={1}
                                            value={item.pricePerTray}
                                            onChange={(v) => updateItem(index, 'pricePerTray', v)}
                                            style={{ width: 150 }}
                                            prefix="KES"
                                        />
                                    </Form.Item>
                                </Space>

                                <Space direction="vertical" align="end">
                                    <div style={{ textAlign: 'right', padding: '0px 0' }}>
                                        <Text strong>Subtotal: KES {computeSubtotal(item).toLocaleString()}</Text>
                                    </div>
                                    <Button
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeItem(index)}
                                        size="small"
                                        disabled={items.length === 1}
                                    >
                                        Remove
                                    </Button>
                                </Space>
                            </Space>
                        </Space>
                    </Card>
                ))}

                <Button type="dashed" icon={<PlusOutlined />} onClick={addItem} style={{ width: '100%' }} disabled={isAddButtonDisabled()}>
                    Add Egg Type
                </Button>

                <Divider />
                <Space direction="vertical" style={{ width: '100%' }}>
                    {/* <Space wrap style={{ width: '100%', justifyContent: 'start' }}> */}
                    {/* <Row gutter={16}> */}
                    <Col span={8}>
                        <Form.Item label="Broken During Sales (Handling)" name="brokenDuringSales">
                            <InputNumber min={0} value={brokenDuringSales} onChange={setBrokenDuringSales} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Form.Item name="images" label="Upload Images">
                        <Upload listType="picture" beforeUpload={() => false} multiple>
                            <Button icon={<UploadOutlined />}>Upload Images</Button>
                        </Upload>
                    </Form.Item>
                    {/* </Space> */}
                </Space>
                <Divider />
                <Form.Item name="paymentMethod" label="Payment Method" rules={[{ required: true }]}>
                    <Select placeholder="Select payment method" options={paymentOptions} />
                </Form.Item>
            </Form>
        </>
    );
}
